export const CALENDAR_EXPORT_MIME = "text/calendar;charset=utf-8";

const SENSITIVE_SOURCE_TYPES = new Set([
  "booking",
  "crm_follow_up",
  "expense",
  "finance_import",
  "model_application",
  "revenue",
  "unified_submission",
]);

function safeExportEvents(events, selectedIds = [], kind = "calendar") {
  const selected = new Set(selectedIds.map(String));
  return events.filter((event) => {
    const sourceType = event.sourceType || event.data?.source_type;
    const isReminder = event.type === "reminder" || event.data?.content_type === "reminder";
    const date = new Date(event.time);
    return Boolean(event.id)
      && !Number.isNaN(date.getTime())
      && !SENSITIVE_SOURCE_TYPES.has(sourceType)
      && (kind !== "reminders" || isReminder)
      && (!selectedIds.length || selected.has(String(event.id)));
  });
}

export function getVisibleCalendarEvents(events, { start, end, platform, status } = {}) {
  const startTime = start ? new Date(start).getTime() : Number.NEGATIVE_INFINITY;
  const endTime = end ? new Date(end).getTime() : Number.POSITIVE_INFINITY;
  const normalizedPlatform = String(platform || "").toLowerCase();
  return events.filter((event) => {
    const time = new Date(event.time).getTime();
    const eventPlatforms = (event.platforms || []).map((value) => String(value).toLowerCase());
    return !Number.isNaN(time)
      && time >= startTime
      && time <= endTime
      && (!normalizedPlatform || eventPlatforms.includes(normalizedPlatform))
      && (!status || event.status === status);
  });
}

export function getCalendarExportEvents(events, selectedIds = [], kind = "calendar") {
  return safeExportEvents(events, selectedIds, kind);
}

function formatDate(date) {
  return date.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
}

function escapeText(value) {
  return String(value ?? "")
    .replace(/\\/g, "\\\\")
    .replace(/\r\n|\r|\n/g, "\\n")
    .replace(/,/g, "\\,")
    .replace(/;/g, "\\;");
}

function foldContentLine(line) {
  const folded = [];
  let segment = "";
  let segmentBytes = 0;
  for (const character of line) {
    const codePoint = character.codePointAt(0);
    const characterBytes = codePoint <= 0x7f ? 1 : codePoint <= 0x7ff ? 2 : codePoint <= 0xffff ? 3 : 4;
    if (segmentBytes + characterBytes > 75) {
      folded.push(segment);
      segment = ` ${character}`;
      segmentBytes = 1 + characterBytes;
    } else {
      segment += character;
      segmentBytes += characterBytes;
    }
  }
  folded.push(segment);
  return folded.join("\r\n");
}

function buildCalendarFile(events, kind) {
  const items = safeExportEvents(events, [], kind).flatMap((event) => {
    const startDate = new Date(event.time);
    const stamp = formatDate(new Date());
    const common = [
      "BEGIN:VTODO",
      `UID:${encodeURIComponent(String(event.id))}@giopilos.com`,
      `DTSTAMP:${stamp}`,
      `DUE:${formatDate(startDate)}`,
      `SUMMARY:${escapeText(event.title)}`,
    ];
    if (kind === "reminders") {
      return [[...common, "STATUS:NEEDS-ACTION", "END:VTODO"].join("\r\n")];
    }
    const endDate = new Date(startDate.getTime() + 60 * 60 * 1000);
    return [[
      "BEGIN:VEVENT",
      `UID:${encodeURIComponent(String(event.id))}@giopilos.com`,
      `DTSTAMP:${stamp}`,
      `DTSTART:${formatDate(startDate)}`,
      `DTEND:${formatDate(endDate)}`,
      `SUMMARY:${escapeText(event.title)}`,
      "END:VEVENT",
    ].join("\r\n")];
  });

  return [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//GioPilos//Content Calendar//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "X-WR-CALNAME:Gio Content Calendar",
    "X-WR-TIMEZONE:UTC",
    ...items,
    "END:VCALENDAR",
    "",
  ].join("\r\n").split("\r\n").map(foldContentLine).join("\r\n");
}

export function buildCalendarIcs(events, selectedIds = []) {
  const selectedEvents = safeExportEvents(events, selectedIds, "calendar");
  return buildCalendarFile(selectedEvents, "calendar");
}

export function buildRemindersIcs(events, selectedIds = []) {
  const selectedEvents = safeExportEvents(events, selectedIds, "reminders");
  return buildCalendarFile(selectedEvents, "reminders");
}

export function getCalendarExportFilename(kind, date = new Date()) {
  const day = new Date(date).toISOString().slice(0, 10);
  return `gio-${kind === "reminders" ? "reminders" : "calendar"}-${day}.ics`;
}

export function downloadCalendarIcs(content, filename, browser = {}) {
  const BlobConstructor = browser.Blob || globalThis.Blob;
  const documentRef = browser.document || globalThis.document;
  const urlApi = browser.urlApi || globalThis.URL;
  if (!BlobConstructor || !documentRef?.body || !urlApi?.createObjectURL || !urlApi?.revokeObjectURL) {
    throw new Error("Calendar downloads are unavailable in this environment");
  }

  const blob = new BlobConstructor([content], { type: CALENDAR_EXPORT_MIME });
  const objectUrl = urlApi.createObjectURL(blob);
  const link = documentRef.createElement("a");
  link.href = objectUrl;
  link.download = filename;
  documentRef.body.appendChild(link);
  try {
    link.click();
  } finally {
    documentRef.body.removeChild(link);
    urlApi.revokeObjectURL(objectUrl);
  }
}

export function isAdminCalendarSession(user, token) {
  if (!user || !token) return false;
  try {
    const encodedPayload = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const payload = JSON.parse(globalThis.atob(encodedPayload));
    const authenticatedUserId = typeof user === "string" ? user : user.id;
    return Boolean(authenticatedUserId && payload.sub)
      && String(payload.sub) === String(authenticatedUserId)
      && (payload.user_type === "admin" || payload.role === "admin");
  } catch {
    return false;
  }
}